import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getEspecialidadesDiscussaoAdminControllerListarQueryKey,
  useEspecialidadesDiscussaoAdminControllerCriar,
} from '@/api/generated/admin-especialidades-de-discussão/admin-especialidades-de-discussão';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

const schema = z.object({
  nome: z.string().trim().min(2, 'Informe pelo menos 2 caracteres').max(120),
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
}

export function EspecialidadeFormDialogo({ aberto, aoFechar }: Props) {
  const queryClient = useQueryClient();
  const criar = useEspecialidadesDiscussaoAdminControllerCriar();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (aberto) reset({ nome: '' });
  }, [aberto, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    try {
      await criar.mutateAsync({ data: { nome: dados.nome } });
      await queryClient.invalidateQueries({
        queryKey: getEspecialidadesDiscussaoAdminControllerListarQueryKey(),
      });
      toast.success('Especialidade cadastrada.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível cadastrar a especialidade.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova especialidade</DialogTitle>
          <DialogDescription>
            Aparece no picker do médico ao abrir uma discussão técnica.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id="nome" rotulo="Nome" erro={errors.nome?.message} obrigatorio dica="Ex.: Endocrinologia, Cardiologia...">
            <Input id="nome" className={CLASSE_CONTROLE} placeholder="Nome da especialidade" {...register('nome')} />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
